// src/oura/components/onboarding/TimelineUploadDrawer.tsx
//
// Upload the Execution Timeline so Oura reads it (Jack, 21 Oct 2026). Two
// files go up together: the PDF the client gets and the data file the Timeline
// build saves next to it. Oura checks the file against the signed quote and
// shows what will change; nothing is saved until staff press "Use this
// Timeline", and the client is not emailed either way.
//
// Staff can correct the values Oura read before they use them (Jack, 23 Oct
// 2026): "Edit" writes them into a copy of the data file and checks it
// again, so the checks and the changes always show what will be saved. The PDF
// is kept as uploaded.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { CheckIcon, FileJsonIcon, FileTextIcon, TriangleAlertIcon, XIcon } from "lucide-react";
import { Drawer, DrawerBlock, DrawerFold, DrawerNotice, DrawerPulse, PillButton, Tag, TYPE } from "@oura/ui";
import { SAMPLE_PHASES } from "@oura/tasks/sample";
import { actionText, codesOf, ownerKind } from "@oura/tasks/model";
import { TASKS_ON } from "@oura/tasks/flag";
import { UPLOAD_MAX_BYTES } from "@service/aeo/clientReview";
import {
    confirmTimeline,
    editedTimelineFile,
    previewTimeline,
    readTimelineData,
    timelineEditCount,
    timelineValues,
    TIMELINE_DATA_MAX_BYTES,
    type TimelineData,
    type TimelineMonth,
    type TimelinePreview,
    type TimelineValues,
    type TimelineWorkItem,
} from "@service/aeo/timelineFile";
import { cn } from "../../lib/cn";

type Stage = { kind: "pick" } | { kind: "checking" } | { kind: "preview"; preview: TimelinePreview };

export function TimelineUploadDrawer({
    open,
    businessId,
    clientName,
    onClose,
    onConfirmed,
}: {
    open: boolean;
    businessId: string;
    clientName: string;
    onClose: () => void;
    /** Oura's plan follows the Timeline now: the card and the plan read again. */
    onConfirmed: (version: string) => void;
}) {
    const [pdf, setPdf] = useState<File | null>(null);
    const [data, setData] = useState<File | null>(null);
    const [stage, setStage] = useState<Stage>({ kind: "pick" });
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    // The data file as built, the values last checked (null: as built) and the form while editing.
    const [built, setBuilt] = useState<TimelineData | null>(null);
    const [values, setValues] = useState<TimelineValues | null>(null);
    const [draft, setDraft] = useState<TimelineValues | null>(null);

    // Bumped on every reset, business change and new request: a response that comes back
    // after any of those is dropped, so a closed check never reopens as a preview and an old
    // preview is never confirmed against another business.
    const requestId = useRef(0);

    const reset = () => {
        requestId.current += 1;
        setPdf(null);
        setData(null);
        setStage({ kind: "pick" });
        setError(null);
        setSaving(false);
        setBuilt(null);
        setValues(null);
        setDraft(null);
    };
    const close = () => { if (saving) return; reset(); onClose(); };
    useEffect(reset, [businessId]);

    // A new file replaces the old one before it is checked: a refused file leaves nothing chosen.
    const pickPdf = (f: File | undefined) => {
        if (!f) return;
        setPdf(null);
        if (!f.name.toLowerCase().endsWith(".pdf")) { setError("The Timeline the client gets is a PDF."); return; }
        if (f.size > UPLOAD_MAX_BYTES) { setError("That PDF is over 20 MB."); return; }
        setError(null);
        setPdf(f);
    };
    const pickData = (f: File | undefined) => {
        if (!f) return;
        setData(null);
        if (!f.name.toLowerCase().endsWith(".json")) { setError("The data file is the .json the Timeline build saved next to the PDF."); return; }
        if (f.size > TIMELINE_DATA_MAX_BYTES) { setError("That data file is too big to be a Timeline's."); return; }
        setError(null);
        setData(f);
    };

    const check = async () => {
        if (!pdf || !data) return;
        const mine = ++requestId.current;
        setError(null);
        setStage({ kind: "checking" });
        const [r, read] = await Promise.all([previewTimeline(businessId, pdf, data), readTimelineData(data)]);
        if (mine !== requestId.current) return;
        if (!r.ok) { setStage({ kind: "pick" }); setError(r.message); return; }
        setBuilt(read);
        setValues(null);
        setStage({ kind: "preview", preview: r.preview });
    };

    // The edited values go through the same check as the file did; the preview only changes when it passes.
    const recheck = async () => {
        if (!pdf || !data || !built || !draft || stage.kind !== "preview") return;
        const edited = editedTimelineFile(built, draft, data.name);
        if (!edited.ok) { setError(edited.message); return; }
        const mine = ++requestId.current;
        const next = draft;
        setError(null);
        setSaving(true);
        const r = await previewTimeline(businessId, pdf, edited.file);
        if (mine !== requestId.current) return;
        setSaving(false);
        if (!r.ok) { setError(r.message); return; }
        setValues(next);
        setDraft(null);
        setStage({ kind: "preview", preview: r.preview });
    };

    const confirm = async () => {
        if (stage.kind !== "preview" || stage.preview.blocked || saving) return;
        const mine = ++requestId.current;
        const checked = stage.preview;
        setSaving(true);
        setError(null);
        const r = await confirmTimeline(businessId, checked.previewId);
        if (mine !== requestId.current) return;
        setSaving(false);
        if (!r.ok) { setError(r.message); return; }
        reset();
        onConfirmed(checked.version);
    };

    const preview = stage.kind === "preview" ? stage.preview : null;
    const edits = built && values ? timelineEditCount(timelineValues(built), values) : 0;
    const foot = preview && draft ? (
        <>
            <PillButton size="sm" disabled={saving} onClick={() => { setDraft(null); setError(null); }}>Cancel</PillButton>
            <PillButton size="sm" variant="primary" busy={saving} onClick={() => void recheck()}>
                {saving ? "Checking…" : "Check again"}
            </PillButton>
        </>
    ) : preview ? (
        preview.blocked ? (
            <PillButton size="sm" variant="primary" onClick={reset}>Choose other files</PillButton>
        ) : (
            <>
                <PillButton size="sm" disabled={saving} onClick={reset}>Back</PillButton>
                {built && (
                    <PillButton size="sm" disabled={saving} onClick={() => { setError(null); setDraft(values ?? timelineValues(built)); }}>Edit</PillButton>
                )}
                <PillButton size="sm" variant="primary" busy={saving} onClick={() => void confirm()}>
                    {saving ? "Saving…" : "Use this Timeline"}
                </PillButton>
            </>
        )
    ) : (
        <>
            <PillButton size="sm" onClick={close}>Cancel</PillButton>
            <PillButton size="sm" variant="primary" disabled={!pdf || !data || stage.kind === "checking"} busy={stage.kind === "checking"} onClick={() => void check()}>
                {stage.kind === "checking" ? "Checking…" : "Check the Timeline"}
            </PillButton>
        </>
    );

    return (
        <Drawer
            open={open}
            onClose={close}
            width={640}
            title="Upload the execution timeline"
            sub={preview
                ? preview.blocked
                    ? "Oura can't use this Timeline. Nothing was saved."
                    : draft
                  ? "Correct what Oura read, then check again. Nothing is saved until you use the Timeline."
                  : `Timeline ${preview.version}. Check what changes, then use it. Nothing is saved until you do.`
                : `Oura reads the Timeline and makes ${clientName}'s plan match it. You see every change before it is saved.`}
            foot={<div className="flex w-full flex-wrap items-center justify-end gap-2">{foot}</div>}
        >
            <div className="flex flex-col gap-3">
                {stage.kind !== "preview" && (
                    <>
                        <FileField
                            id="oura-timeline-pdf"
                            icon={<FileTextIcon aria-hidden size={18} className="text-gray-500" />}
                            label="Timeline PDF"
                            note="What the client gets."
                            accept=".pdf"
                            file={pdf}
                            disabled={stage.kind === "checking"}
                            onPick={pickPdf}
                        />
                        <FileField
                            id="oura-timeline-data"
                            icon={<FileJsonIcon aria-hidden size={18} className="text-gray-500" />}
                            label="Timeline data file"
                            note="What Oura reads. The .json saved next to the PDF when the Timeline was built."
                            accept=".json,application/json"
                            file={data}
                            disabled={stage.kind === "checking"}
                            onPick={pickData}
                        />
                        {stage.kind === "checking" && (
                            <DrawerBlock label="Checking against the signed quote">
                                <DrawerPulse lines={3} label="Checking the Timeline…" />
                            </DrawerBlock>
                        )}
                    </>
                )}

                {preview && draft && <EditValues preview={preview} draft={draft} disabled={saving} onChange={setDraft} />}
                {preview && !draft && edits > 0 && (
                    <DrawerNotice>
                        <p className="m-0 font-medium">You changed {edits === 1 ? "1 value" : `${edits} values`} from the Timeline file.</p>
                        <p className="m-0 mt-1">Oura's plan uses your values. The PDF stays as uploaded, so rebuild it if {clientName} should see them too.</p>
                    </DrawerNotice>
                )}
                {preview && !draft && <Checks preview={preview} />}
                {preview && !draft && !preview.blocked && <Changes preview={preview} />}
                {TASKS_ON && preview && !draft && !preview.blocked && <TaskBreakdown />}
                {preview && !draft && !preview.blocked && (
                    <p className={cn(TYPE.tc, "m-0 px-1 text-gray-500")}>
                        No email goes to {clientName}. They see the plan when you press Send.{built ? " To correct anything Oura read, press Edit." : ""}
                    </p>
                )}
                {error && <p role="alert" className={cn(TYPE.tb, "m-0 px-1 font-medium text-rose-600")}>{error}</p>}
            </div>
        </Drawer>
    );
}

const FIELD = "w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50";

const CADENCES: Array<[string, string]> = [["monthly", "Every month"], ["quarterly", "Every 3 months"], ["yearly", "Every 12 months"]];

/** The form behind "Edit": the plan's values as the file has them, with Oura's today beside them. */
function EditValues({ preview, draft, disabled, onChange }: {
    preview: TimelinePreview;
    draft: TimelineValues;
    disabled: boolean;
    onChange: (next: TimelineValues) => void;
}) {
    const now = (key: string) => preview.changes.find((c) => c.key === key)?.before ?? "Not set";
    const set = <K extends keyof TimelineValues>(key: K, value: TimelineValues[K]) => onChange({ ...draft, [key]: value });
    const setMonth = (month: number, patch: Partial<TimelineValues["months"][number]>) =>
        set("months", draft.months.map((m) => (m.month === month ? { ...m, ...patch } : m)));
    const cadences = CADENCES.some(([v]) => v === draft.ai_check) || !draft.ai_check ? CADENCES : [...CADENCES, [draft.ai_check, draft.ai_check] as [string, string]];
    const date = (key: "start_date" | "work_starts" | "handover" | "term_ends", label: string, change: string) => (
        <EditRow label={label} now={now(change)}>
            <input type="date" aria-label={label} value={draft[key]} disabled={disabled} onChange={(e) => set(key, e.target.value)} className={FIELD} />
        </EditRow>
    );
    return (
        <>
            <DrawerBlock label="What Oura will use">
                <Table head={["", "Oura now", "Use"]}>
                    {date("start_date", "Start date", "start")}
                    {date("work_starts", "Work starts", "work")}
                    {date("handover", "Handover", "handover")}
                    {date("term_ends", "Term ends", "end")}
                    <EditRow label="AI check" now={now("check")}>
                        <select aria-label="AI check" value={draft.ai_check} disabled={disabled} onChange={(e) => set("ai_check", e.target.value)} className={FIELD}>
                            {cadences.map(([v, word]) => <option key={v} value={v}>{word}</option>)}
                        </select>
                        {draft.ai_check === "monthly" && (
                            <p className={cn(TYPE.tc, "m-0 mt-1 text-amber-800")}>Each AI check costs money. Monthly runs it 3 times as often as every 3 months.</p>
                        )}
                    </EditRow>
                    <EditRow label="Signature questions" now={now("signature")}>
                        <input type="text" aria-label="Signature questions" placeholder="12, 31" value={draft.signature_questions} disabled={disabled} onChange={(e) => set("signature_questions", e.target.value)} className={FIELD} />
                    </EditRow>
                    <EditRow label="Priority keywords" now={now("keywords")}>
                        <textarea aria-label="Priority keywords" rows={4} placeholder="One per line" value={draft.priority_keywords} disabled={disabled} onChange={(e) => set("priority_keywords", e.target.value)} className={cn(FIELD, "resize-y")} />
                    </EditRow>
                </Table>
            </DrawerBlock>
            {draft.months.length > 0 && (
                <DrawerBlock label="Month by month">
                    <Table head={["", "Report", "Answers live"]}>
                        {draft.months.map((m) => (
                            <tr key={m.month} className="border-t border-gray-100">
                                <th scope="row" className="py-2 pr-3 text-left align-top font-normal text-gray-600">Month {m.month}</th>
                                <td className="py-2 pr-3 align-top">
                                    <input type="date" aria-label={`Month ${m.month} report`} value={m.report_on} disabled={disabled} onChange={(e) => setMonth(m.month, { report_on: e.target.value })} className={FIELD} />
                                </td>
                                <td className="py-2 pr-3 align-top">
                                    <input type="number" min={0} step={1} inputMode="numeric" aria-label={`Month ${m.month} answers`} value={m.answers} disabled={disabled} onChange={(e) => setMonth(m.month, { answers: e.target.value })} className={cn(FIELD, "max-w-[96px]")} />
                                </td>
                            </tr>
                        ))}
                    </Table>
                </DrawerBlock>
            )}
        </>
    );
}

function EditRow({ label, now, children }: { label: string; now: string; children: ReactNode }) {
    return (
        <tr className="border-t border-gray-100">
            <th scope="row" className="py-2 pr-3 text-left align-top font-normal text-gray-600">{label}</th>
            <td className="py-2 pr-3 align-top text-gray-500">{now}</td>
            <td className="w-[55%] py-2 pr-3 align-top">{children}</td>
        </tr>
    );
}

function FileField({ id, icon, label, note, accept, file, disabled, onPick }: {
    id: string;
    icon: ReactNode;
    label: string;
    note: string;
    accept: string;
    file: File | null;
    disabled?: boolean;
    onPick: (f: File | undefined) => void;
}) {
    return (
        <DrawerBlock>
            <div className="flex items-start gap-3">
                <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg bg-gray-50">{icon}</span>
                <div className="min-w-0 flex-1">
                    <label htmlFor={id} className={cn(TYPE.tb, "block font-medium text-gray-900")}>{label}</label>
                    <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{file ? file.name : note}</p>
                </div>
                <label
                    className={cn(
                        "shrink-0 cursor-pointer rounded-full px-3 py-1.5 text-[13px] font-medium ring-1 ring-inset",
                        file ? "text-gray-700 ring-gray-200 hover:bg-gray-50" : "text-blue-700 ring-blue-200 hover:bg-blue-50",
                        disabled && "pointer-events-none opacity-50",
                    )}
                >
                    {file ? "Change" : "Choose file"}
                    <input
                        id={id}
                        type="file"
                        accept={accept}
                        disabled={disabled}
                        className="sr-only"
                        onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = ""; }}
                    />
                </label>
            </div>
        </DrawerBlock>
    );
}

function Checks({ preview }: { preview: TimelinePreview }) {
    const differs = preview.checks.filter((c) => !c.ok && !c.blocks).length;
    return (
        <>
            {preview.blocked ? (
                <DrawerNotice>
                    <p className="m-0 font-medium">This Timeline is for another quote, so Oura's plan stays as it is.</p>
                    <p className="m-0 mt-1">Rebuild it from this client's quote, then upload the new PDF and data file.</p>
                </DrawerNotice>
            ) : differs > 0 && (
                <DrawerNotice>
                    <p className="m-0 font-medium">{differs === 1 ? "One thing differs" : `${differs} things differ`} from the quote.</p>
                    <p className="m-0 mt-1">You can still use this Timeline. Check it is meant to, then go ahead.</p>
                </DrawerNotice>
            )}
            <DrawerBlock label={`Checked against quote ${preview.reference}`}>
                <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                    {preview.checks.map((c) => (
                        <li key={c.key} className="flex items-start gap-2">
                            {c.ok
                                ? <CheckIcon aria-label="Matches" size={16} className="mt-0.5 shrink-0 text-emerald-600" />
                                : c.blocks
                                  ? <XIcon aria-label="Blocks" size={16} className="mt-0.5 shrink-0 text-rose-600" />
                                  : <TriangleAlertIcon aria-label="Differs" size={16} className="mt-0.5 shrink-0 text-amber-600" />}
                            <span className={cn(c.ok ? "text-gray-700" : c.blocks ? "font-medium text-rose-700" : "font-medium text-amber-800")}>{c.said}</span>
                        </li>
                    ))}
                </ul>
            </DrawerBlock>
        </>
    );
}

function Changes({ preview }: { preview: TimelinePreview }) {
    const changed = preview.changes.filter((c) => c.before !== c.after).length
        + preview.months.filter((m) => m.reportBefore !== m.reportAfter || m.answersBefore !== m.answersAfter
            || m.work.some((w) => workChange(w) !== null)).length;
    return (
        <>
            <DrawerBlock label={changed ? "What changes in Oura" : "Nothing changes in Oura"}>
                <Table head={["", "Oura now", "From the Timeline"]}>
                    {preview.changes.map((c) => (
                        <Row key={c.key} label={c.label} before={c.before} after={c.after} />
                    ))}
                </Table>
            </DrawerBlock>
            <DrawerBlock label="Month by month">
                <Table head={["", "Report", "Answers live"]}>
                    {preview.months.map((m) => (
                        <tr key={m.month} className="border-t border-gray-100">
                            <th scope="row" className="py-2 pr-3 text-left font-normal text-gray-600">Month {m.month}</th>
                            <Cell before={m.reportBefore} after={m.reportAfter} />
                            <Cell before={m.answersBefore === null ? null : String(m.answersBefore)} after={m.answersAfter === null ? null : String(m.answersAfter)} />
                        </tr>
                    ))}
                </Table>
            </DrawerBlock>
            <p className={cn(TYPE.lab, "m-0 mt-2 px-1")}>Work, month by month</p>
            {preview.months.map((m) => <MonthWork key={m.month} month={m} />)}
        </>
    );
}

/** What the Timeline does to one piece of work, or null when it stays as it is. */
function workChange(w: TimelineWorkItem): "added" | "dropped" | "changed" | null {
    if (!w.inOura) return "added";
    if (!w.inTimeline) return "dropped";
    return w.before !== w.after ? "changed" : null;
}

const CHANGE_TAG = { added: { tone: "blue", word: "Added" }, dropped: { tone: "rose", word: "Dropped" }, changed: { tone: "amber", word: "Changed" } } as const;

/** One month's work, folded, with its changes counted on the head. Month 1 opens, since its work starts first. */
function MonthWork({ month: m }: { month: TimelineMonth }) {
    const kept = m.work.filter((w) => w.inTimeline).length;
    const changes = m.work.filter((w) => workChange(w) !== null).length;
    const span = m.from && m.to ? `, ${m.from} to ${m.to}` : "";
    return (
        <DrawerFold
            id={`timeline-month-${m.month}`}
            label={`Month ${m.month}${span}`}
            defaultOpen={m.month === 1}
            note={`${kept} ${kept === 1 ? "item" : "items"}${changes ? `, ${changes} ${changes === 1 ? "change" : "changes"}` : ""}`}
        >
            <ul className="m-0 flex list-none flex-col p-0">
                {m.work.map((w) => {
                    const change = workChange(w);
                    return (
                        <li key={w.code} className="flex items-start gap-3 border-t border-gray-100 py-2 first:border-t-0">
                            <div className="min-w-0 flex-1">
                                <p className={cn("m-0", change === "dropped" ? "text-gray-400 line-through" : "text-gray-900")}>{w.label}</p>
                                {(w.before || w.after) && (
                                    <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                                        {change === "changed" && w.before ? <><span className="line-through">{w.before}</span> {w.after ?? "Not set"}</> : w.after ?? w.before}
                                    </p>
                                )}
                            </div>
                            {change && <Tag tone={CHANGE_TAG[change].tone} className="mt-0.5 shrink-0">{CHANGE_TAG[change].word}</Tag>}
                        </li>
                    );
                })}
            </ul>
        </DrawerFold>
    );
}

function Table({ head, children }: { head: string[]; children: ReactNode }) {
    return (
        <table className="w-full border-collapse text-sm tabular-nums">
            <thead>
                <tr>
                    {head.map((h, i) => (
                        <th key={i} scope="col" className={cn(TYPE.tc, "pb-1.5 pr-3 text-left font-medium text-gray-500")}>{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody>{children}</tbody>
        </table>
    );
}

function Row({ label, before, after }: { label: string; before: string | null; after: string | null }) {
    const same = before === after;
    return (
        <tr className="border-t border-gray-100">
            <th scope="row" className="py-2 pr-3 text-left font-normal text-gray-600">{label}</th>
            <td className={cn("py-2 pr-3", same ? "text-gray-900" : "text-gray-400 line-through")}>{before ?? "Not set"}</td>
            <td className={cn("py-2 pr-3", same ? "text-gray-500" : "font-medium text-gray-900")}>
                {same ? "No change" : after ?? "Not set"}
            </td>
        </tr>
    );
}

/** One "now → Timeline" cell: the old value struck through only when it changes. */
function Cell({ before, after }: { before: string | null; after: string | null }) {
    if (before === after) return <td className="py-2 pr-3 text-gray-900">{after ?? "Not set"}</td>;
    return (
        <td className="py-2 pr-3">
            {before !== null && <span className="mr-1.5 text-gray-400 line-through">{before}</span>}
            <span className="font-medium text-gray-900">{after ?? "Not set"}</span>
        </td>
    );
}

/**
 * The task breakdown Oura made from the Timeline (Angel's Master Tracker
 * rules): one fold per phase. Sample rows, not saved anywhere, until the
 * backend makes and stores the real tasks.
 */
function TaskBreakdown() {
    const total = SAMPLE_PHASES.reduce((n, p) => n + p.tasks.length, 0);
    return (
        <div>
            <p className={cn(TYPE.lab, "m-0 mt-2 px-1")}>Sample task preview · {total}</p>
            <p className={cn(TYPE.tc, "m-0 mb-2 px-1 text-gray-600")}>These sample tasks were not made from this upload. Using this Timeline does not save them or write them to a Master Tracker sheet.</p>
            {SAMPLE_PHASES.map((p, i) => (
                <DrawerFold key={p.key} id={`timeline-tasks-${p.key}`} label={p.name} defaultOpen={i === 1} note={`${p.tasks.length} tasks, ${p.tasks.filter((t) => ownerKind(t.owner) !== "us").length} for the client`}>
                    <ul className="m-0 flex list-none flex-col p-0">
                        {p.tasks.map((t) => (
                            <li key={t.id} className="flex items-start gap-3 border-t border-gray-100 py-2 first:border-t-0">
                                <span className="min-w-7 shrink-0 pt-0.5 text-xs font-semibold text-blue-700">{codesOf(t.action).join(" ")}</span>
                                <p className="m-0 min-w-0 flex-1 text-gray-900">{actionText(t.action)}</p>
                                <span className={cn(TYPE.tc, "shrink-0 whitespace-nowrap text-gray-500")}>{ownerKind(t.owner) === "us" ? "" : "Client · "}{t.target.replace(/ 20\d\d$/, "")}</span>
                            </li>
                        ))}
                    </ul>
                </DrawerFold>
            ))}
        </div>
    );
}
